// Cmd-K command palette for the v2 shell.
//
// cmdk handles fuzzy matching, accessibility, and keyboard nav so we
// don't have to. The palette opens on Cmd/Ctrl-K (registered in the
// shell), Esc closes. All commands are grouped: Navigation, Create,
// Theme. Adding a new command is just another <Command.Item>.

import { Command } from 'cmdk';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth.js';
import { useTheme } from '../theme.js';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange(open: boolean): void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const theme = useTheme();
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  function run(action: () => void) {
    onOpenChange(false);
    action();
  }

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-[15vh] px-4">
      <div
        className="absolute inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-[2px]"
        onClick={() => onOpenChange(false)}
        aria-hidden
      />
      <Command
        label="Command palette"
        className="relative w-full max-w-lg surface border border-divider rounded-xl shadow-2xl overflow-hidden"
      >
        <Command.Input
          autoFocus
          value={search}
          onValueChange={setSearch}
          placeholder="Type a command…"
          className="w-full px-4 py-3 text-sm bg-transparent border-b border-divider focus:outline-none placeholder:text-faint"
        />
        <Command.List className="max-h-[60vh] overflow-y-auto p-1">
          <Command.Empty className="px-4 py-6 text-sm text-muted text-center">
            No matching commands.
          </Command.Empty>

          <Command.Group heading="Navigate">
            <Item label="Go to People" hint="People" onSelect={() => run(() => navigate('/people'))} />
            <Item label="Go to Companies" hint="Companies" onSelect={() => run(() => navigate('/companies'))} />
            <Item label="Go to Lists" hint="Lists" onSelect={() => run(() => navigate('/dial'))} />
            <Item label="Go to Activity" hint="Audit" onSelect={() => run(() => navigate('/audit'))} />
            <Item label="Go to Colleagues" hint="Graph" onSelect={() => run(() => navigate('/colleagues'))} />
            <Item label="Go to Trash" onSelect={() => run(() => navigate('/trash'))} />
            <Item label="Open Settings" onSelect={() => run(() => navigate('/settings'))} />
          </Command.Group>

          <Command.Group heading="Create">
            <Item
              label="New person"
              onSelect={() => run(() => navigate('/contacts/new?kind=person'))}
            />
            <Item
              label="New company"
              onSelect={() => run(() => navigate('/contacts/new?kind=company'))}
            />
            <Item label="New list" onSelect={() => run(() => navigate('/dial'))} />
          </Command.Group>

          <Command.Group heading="Theme">
            <Item
              label="Theme: Light"
              onSelect={() => run(() => theme.setPreference('light'))}
              hint={theme.preference === 'light' ? 'current' : undefined}
            />
            <Item
              label="Theme: Dark"
              onSelect={() => run(() => theme.setPreference('dark'))}
              hint={theme.preference === 'dark' ? 'current' : undefined}
            />
            <Item
              label="Theme: System"
              onSelect={() => run(() => theme.setPreference('system'))}
              hint={theme.preference === 'system' ? 'current' : undefined}
            />
          </Command.Group>

          <Command.Group heading="Account">
            <Item label="Sign out" onSelect={() => run(signOut)} />
          </Command.Group>
        </Command.List>
        <div className="border-t border-divider px-3 py-2 text-[11px] text-faint flex items-center justify-between">
          <span>↑↓ navigate · ↵ select · Esc close</span>
          <span className="font-mono">⌘K</span>
        </div>
      </Command>
    </div>,
    document.body
  );
}

function Item({
  label,
  hint,
  onSelect
}: {
  label: string;
  hint?: string;
  onSelect(): void;
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      className="flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-md cursor-pointer aria-selected:surface-muted aria-selected:text-strong text-mid"
    >
      <span>{label}</span>
      {hint && <span className="text-xs text-faint">{hint}</span>}
    </Command.Item>
  );
}
